import React from 'react';
import { Box } from '@mui/material';
import {
  MicNone as MicIcon,
  PhotoCameraOutlined as PhotoIcon,
  TextFields as TextIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Card, Container, Section, SectionHeader } from './primitives';
import { formatBRL } from './richText';
import { BOT_USERNAME, landing } from './tokens';

const StepNumber: React.FC<{ n: number }> = ({ n }) => (
  <Box
    component='span'
    aria-hidden='true'
    sx={{
      fontFamily: landing.display,
      fontSize: 44,
      fontWeight: 700,
      lineHeight: 1,
      color: landing.accent,
    }}
  >
    {n}
  </Box>
);

const StepTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='h3' sx={{ fontSize: 23, letterSpacing: '-0.015em' }}>
    {children}
  </Box>
);

const StepText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box component='p' sx={{ color: landing.ink2, lineHeight: 1.6 }}>
    {children}
  </Box>
);

const inputChips = [
  { key: 'text', icon: <TextIcon /> },
  { key: 'voice', icon: <MicIcon /> },
  { key: 'photo', icon: <PhotoIcon /> },
];

const HowItWorks: React.FC = () => {
  const { t, i18n } = useTranslation('landing');
  const bars = [
    { key: 'food', amount: 1212.4, width: '74%' },
    { key: 'transport', amount: 586.3, width: '36%' },
  ];
  return (
    <Section id='como-funciona' sx={{ pb: { xs: 5, md: 7 } }}>
      <Container sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <SectionHeader
          eyebrow={t('howItWorks.eyebrow')}
          title={t('howItWorks.title')}
        />
        <Box
          component='ol'
          sx={{
            listStyle: 'none',
            m: 0,
            p: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
            gap: 2.5,
          }}
        >
          <Card component='li'>
            <StepNumber n={1} />
            <StepTitle>{t('howItWorks.open.title')}</StepTitle>
            <StepText>{t('howItWorks.open.text')}</StepText>
            <Box
              sx={{
                mt: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1.5,
                p: '12px 12px 12px 16px',
                borderRadius: '16px',
                bgcolor: landing.bg2,
              }}
            >
              <Box
                component='span'
                sx={{
                  fontSize: 14,
                  color: landing.ink2,
                  fontFamily: landing.mono,
                  overflowWrap: 'anywhere',
                }}
              >
                t.me/{BOT_USERNAME}
              </Box>
              <Box
                component='span'
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  minHeight: 36,
                  px: 1.75,
                  borderRadius: '999px',
                  bgcolor: landing.accent,
                  color: '#FFFFFF',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {t('howItWorks.open.start')}
              </Box>
            </Box>
          </Card>

          <Card component='li'>
            <StepNumber n={2} />
            <StepTitle>{t('howItWorks.tell.title')}</StepTitle>
            <StepText>{t('howItWorks.tell.text')}</StepText>
            <Box sx={{ mt: 'auto', display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {inputChips.map((chip) => (
                <Box
                  key={chip.key}
                  component='span'
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                    minHeight: 40,
                    px: 1.75,
                    borderRadius: '999px',
                    bgcolor: landing.bg2,
                    fontSize: 14,
                    fontWeight: 600,
                    color: landing.ink2,
                    '& svg': { fontSize: 17 },
                  }}
                >
                  {chip.icon}
                  {t(`howItWorks.tell.chips.${chip.key}`)}
                </Box>
              ))}
            </Box>
          </Card>

          <Card component='li'>
            <StepNumber n={3} />
            <StepTitle>{t('howItWorks.see.title')}</StepTitle>
            <StepText>{t('howItWorks.see.text')}</StepText>
            <Box
              sx={{
                mt: 'auto',
                display: 'flex',
                flexDirection: 'column',
                gap: 1.25,
                p: '14px 16px',
                borderRadius: '16px',
                bgcolor: landing.bg2,
              }}
            >
              {bars.map((bar) => (
                <Box key={bar.key} sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                    <Box component='span' sx={{ color: landing.ink2 }}>
                      {t(`howItWorks.see.${bar.key}`)}
                    </Box>
                    <strong>{formatBRL(bar.amount, i18n.language)}</strong>
                  </Box>
                  <Box sx={{ height: 6, borderRadius: 99, bgcolor: landing.line }}>
                    <Box
                      sx={{
                        width: bar.width,
                        height: 6,
                        borderRadius: 99,
                        bgcolor: landing.accent,
                      }}
                    />
                  </Box>
                </Box>
              ))}
            </Box>
          </Card>
        </Box>
      </Container>
    </Section>
  );
};

export default HowItWorks;
